import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import { useCountry, PAISES_UI } from "@/context/CountryContext";

export const flagUrl = (code) => `https://flagcdn.com/w40/${code.toLowerCase()}.png`;

export const CountrySelector = ({ light = false }) => {
  const { pais, setPais } = useCountry();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const select = (p) => {
    setOpen(false);
    if (p.code === pais) return;
    setPais(p.code);
    toast.info(`Operando bajo legislación de ${p.nombre} · ${p.registro} (${p.moneda})`);
  };

  return (
    <div className="relative" ref={ref} data-testid="country-selector">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="País de operación"
        data-testid="country-selector-btn"
        className={`inline-flex items-center gap-1.5 h-11 sm:h-9 px-2 rounded-md border transition active:scale-[0.94] ${
          light
            ? "border-white/20 text-white/90 hover:text-white hover:border-white/40"
            : "border-slate-200 text-slate-600 hover:text-[#13315C] hover:border-[#F5B301]"
        }`}
      >
        <img src={flagUrl(pais)} alt={pais} className="w-5 h-auto rounded-[2px]" />
        <span className="text-xs font-bold hidden sm:inline">{pais}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden z-50" role="listbox" data-testid="country-menu">
          <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-bold text-slate-400">País de operación</p>
          {PAISES_UI.map((p) => (
            <button
              key={p.code}
              type="button"
              onClick={() => select(p)}
              role="option"
              aria-selected={p.code === pais}
              data-testid={`country-option-${p.code}`}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
            >
              <img src={flagUrl(p.code)} alt={p.nombre} className="w-6 h-auto rounded-[2px] shrink-0" />
              <span className="flex-1">
                <span className="block text-sm font-semibold text-[#13315C]">{p.nombre}</span>
                <span className="block text-[10px] text-slate-400">{p.registro} · {p.moneda}</span>
              </span>
              {p.code === pais && <Check size={15} className="text-[#10B981]" strokeWidth={3} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
